import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';
import { ConfigService } from './config.service';

// ─── Types ──────────────────────────────────────────────────────────────
export type SiteStatus = 'draft' | 'live' | 'archived' | 'suspended';
export type SitePlan = 'starter' | 'pro' | 'business';
export type DomainKind = 'subdomain' | 'custom';

export interface Site {
  id: string;
  vendor_id: number;
  slug: string;
  name: string;
  plan: SitePlan;
  status: SiteStatus;
  primary_locale: string;
  primary_currency: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

export interface SiteSettings {
  site_id: string;
  theme: 'light' | 'dark' | 'auto';
  palette: { primary?: string; accent?: string; bg?: string };
  font_pair: { sans?: string; serif?: string };
  density: 'compact' | 'comfortable' | 'spacious';
  radius: 'none' | 'small' | 'medium' | 'large';
  seo_global: Record<string, unknown>;
  integrations: {
    payment?: any;
    shipping?: any;
    chat?: any;
    social?: any;
  };
}

export interface SiteTemplate {
  id: string;
  slug: string;
  name: string;
  category: string;
  preview_url?: string;
  description?: string;
}

export interface SitePage {
  id: string;
  site_id: string;
  path: string;
  title: string;
  layout: string;
  draft: boolean;
  sort: number;
}

export interface SiteBlock {
  id: string;
  page_id: string;
  type: string;
  props_json: any;
  content_md?: string;
  sort: number;
  draft: boolean;
}

export interface DnsRecordSpec {
  record_type: string;
  name: string;
  value: string;
  ttl?: number;
  proxied?: boolean;
  note?: string;
}

export interface DnsInstructions {
  recommended?: 'delegate' | 'dns_only';
  delegate?: { method?: string; nameservers?: string[]; note?: string };
  dns_only?: {
    verification?: DnsRecordSpec;
    routing?: DnsRecordSpec;
  };
}

export interface SiteDomain {
  id: string;
  site_id: string;
  domain: string;
  kind: DomainKind;
  ssl_status: string;
  dns_token?: string;
  dns_verified_at?: string;
  is_primary: boolean;
  dns_instructions?: DnsInstructions | null;
}

export interface DomainSearchResult {
  name: string;
  registrable: boolean;
  tier?: string | null;
  reason?: string | null;
  currency: string;
  price?: number | null;        // prix client (avec marge), USD
  price_credits?: number | null; // crédits à débiter
}

export interface SiteSuggestion {
  priority: 'high' | 'medium' | 'low' | string;
  key: string;
  why: string;
  suggested_action: string;
}

export interface SiteAIThread {
  id: string;
  site_id: string;
  user_id: number;
  title?: string;
  status: 'open' | 'closed' | 'escalated';
  summary?: string;
  created_at: string;
  updated_at: string;
}

export interface SiteAIMessage {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  tool_calls_json?: any;
  tool_name?: string;
  created_at: string;
}

export interface SiteAITool {
  name: string;
  description?: string;
  category?: string;
}

export interface StudioCreditsTier {
  id: string; // standard | pro | ultra (+ alias premium_opus | premium_fable)
  label: string;
  model: string | null;
  price: number;
  /** Crédits/minute du tier (facturation temps réel — 0 pour standard). */
  rate_per_min?: number;
}

export interface StudioCredits {
  balance: number;
  price_per_premium_turn: number;
  tiers: StudioCreditsTier[];
}

export interface VendorAISecret {
  id: number;
  key: string;
  label: string;
  description?: string;
  value: string; // toujours masqué côté API (••••••)
  created_at: string;
  updated_at: string;
}

// ─── Payloads ───────────────────────────────────────────────────────────
export interface SiteCreatePayload {
  name: string;
  slug?: string;
  primary_locale?: string;
  primary_currency?: string;
  plan?: SitePlan;
  /** Champs libres collectés au wizard (vision, audience, tone, etc.) */
  meta?: Record<string, any>;
}

export interface SiteSettingsPatch {
  theme?: 'light' | 'dark' | 'auto';
  palette?: { primary?: string; secondary?: string; accent?: string; bg?: string; text?: string };
  font_pair?: { heading?: string; body?: string; sans?: string; serif?: string };
  density?: 'compact' | 'comfortable' | 'spacious';
  radius?: 'none' | 'small' | 'medium' | 'large';
  seo_global?: Record<string, unknown>;
  integrations?: Record<string, unknown>;
}

export interface SiteUpdatePayload {
  name?: string;
  slug?: string;
  status?: SiteStatus;
  plan?: SitePlan;
  primary_locale?: string;
  primary_currency?: string;
  settings_patch?: SiteSettingsPatch;
}

export interface ApplyTemplatePayload {
  template_slug: string;
  options?: Record<string, any>;
}

export interface LinkStoresPayload {
  store_ids: Array<string | number>;
}

@Injectable({ providedIn: 'root' })
export class SiteBuilderService {
  private readonly http = inject(HttpClient);
  private readonly cookies = inject(CookieService);
  private readonly cfg = inject(ConfigService);

  private readonly base = `${this.cfg.apiUrl}/sites`;

  // ── HTTP helpers ─────────────────────────────────────────────────────
  private createAuthHeaders(extra: Record<string, string> = {}): HttpHeaders {
    const token = this.cookies.get('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      ...extra,
    });
  }

  private jsonHeaders(): HttpHeaders {
    return this.createAuthHeaders({ 'Content-Type': 'application/json' });
  }

  private withErrors<T>(label: string) {
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        timeout(15000),
        catchError((err) => {
          let message = `Erreur ${label}.`;
          if (err?.name === 'TimeoutError') {
            message = `Le serveur met trop de temps à répondre (${label}).`;
          } else if (err?.status === 401 || err?.status === 403) {
            message = 'Authentification échouée. Veuillez vous reconnecter.';
          } else if (err?.status === 404) {
            message = `Ressource introuvable (${label}).`;
          } else if (err?.status === 409) {
            message = `Conflit (${label}). Le slug ou domaine est déjà utilisé.`;
          } else if (err?.error?.detail) {
            message = String(err.error.detail);
          }
          return throwError(() => ({ ...err, customMessage: message }));
        }),
      );
  }

  // ── Sites CRUD ───────────────────────────────────────────────────────
  createSite(payload: SiteCreatePayload): Observable<Site> {
    return this.http
      .post<Site>(`${this.base}/`, payload, { headers: this.jsonHeaders() })
      .pipe(this.withErrors('création du site'));
  }

  listSites(): Observable<Site[]> {
    return this.http
      .get<Site[]>(`${this.base}/`, { headers: this.createAuthHeaders() })
      .pipe(this.withErrors('chargement des sites'));
  }

  getSite(id: string): Observable<Site> {
    return this.http
      .get<Site>(`${this.base}/${id}`, { headers: this.createAuthHeaders() })
      .pipe(this.withErrors('chargement du site'));
  }

  updateSite(id: string, payload: SiteUpdatePayload): Observable<Site> {
    return this.http
      .patch<Site>(`${this.base}/${id}`, payload, { headers: this.jsonHeaders() })
      .pipe(this.withErrors('mise à jour du site'));
  }

  archiveSite(id: string): Observable<{ deleted: boolean }> {
    return this.http
      .delete<{ deleted: boolean }>(`${this.base}/${id}`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors("archivage du site"));
  }

  linkStores(id: string, payload: LinkStoresPayload): Observable<any> {
    return this.http
      .post(`${this.base}/${id}/stores/link`, payload, { headers: this.jsonHeaders() })
      .pipe(this.withErrors('liaison des boutiques'));
  }

  // ── Templates ────────────────────────────────────────────────────────
  listTemplates(): Observable<SiteTemplate[]> {
    return this.http
      .get<SiteTemplate[]>(`${this.base}/templates/`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des templates'));
  }

  getTemplate(slug: string): Observable<SiteTemplate> {
    return this.http
      .get<SiteTemplate>(`${this.base}/templates/${slug}`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement du template'));
  }

  applyTemplate(id: string, payload: ApplyTemplatePayload): Observable<any> {
    return this.http
      .post(`${this.base}/${id}/apply-template`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors("application du template"));
  }

  // ── Pages ────────────────────────────────────────────────────────────
  listPages(siteId: string): Observable<SitePage[]> {
    return this.http
      .get<SitePage[]>(`${this.base}/${siteId}/pages/`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des pages'));
  }

  createPage(siteId: string, payload: Partial<SitePage>): Observable<SitePage> {
    return this.http
      .post<SitePage>(`${this.base}/${siteId}/pages/`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('création de la page'));
  }

  updatePage(
    siteId: string,
    pageId: string,
    payload: Partial<SitePage>,
  ): Observable<SitePage> {
    return this.http
      .patch<SitePage>(`${this.base}/${siteId}/pages/${pageId}`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('mise à jour de la page'));
  }

  deletePage(siteId: string, pageId: string): Observable<any> {
    return this.http
      .delete(`${this.base}/${siteId}/pages/${pageId}`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('suppression de la page'));
  }

  publishPage(siteId: string, pageId: string): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/pages/${pageId}/publish`, {}, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('publication de la page'));
  }

  /**
   * Publication au niveau SITE. Le backend choisit le mode :
   * - site en FICHIERS (SPA Vite) → build + promotion dist/ → published/ ;
   * - site legacy blocs → publish-all.
   */
  publishSite(siteId: string): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/publish`, {}, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('publication du site'));
  }

  // ── Blocks ───────────────────────────────────────────────────────────
  listBlocks(siteId: string, pageId: string): Observable<SiteBlock[]> {
    return this.http
      .get<SiteBlock[]>(`${this.base}/${siteId}/pages/${pageId}/blocks/`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des blocs'));
  }

  createBlock(
    siteId: string,
    pageId: string,
    payload: Partial<SiteBlock>,
  ): Observable<SiteBlock> {
    return this.http
      .post<SiteBlock>(`${this.base}/${siteId}/pages/${pageId}/blocks/`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('création du bloc'));
  }

  updateBlock(
    siteId: string,
    blockId: string,
    payload: Partial<SiteBlock>,
  ): Observable<SiteBlock> {
    return this.http
      .patch<SiteBlock>(`${this.base}/${siteId}/blocks/${blockId}`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('mise à jour du bloc'));
  }

  deleteBlock(siteId: string, blockId: string): Observable<any> {
    return this.http
      .delete(`${this.base}/${siteId}/blocks/${blockId}`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('suppression du bloc'));
  }

  reorderBlocks(
    siteId: string,
    payload: { page_id: string; order: string[] },
  ): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/blocks/reorder`, payload, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('réorganisation des blocs'));
  }

  // ── Domains ──────────────────────────────────────────────────────────
  listDomains(siteId: string): Observable<SiteDomain[]> {
    return this.http
      .get<SiteDomain[]>(`${this.base}/${siteId}/domains/`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des domaines'));
  }

  setSubdomain(siteId: string, subdomain: string): Observable<SiteDomain> {
    return this.http
      .post<SiteDomain>(
        `${this.base}/${siteId}/domains/subdomain`,
        { subdomain },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("configuration du sous-domaine"));
  }

  setCustomDomain(siteId: string, domain: string): Observable<SiteDomain> {
    return this.http
      .post<SiteDomain>(
        `${this.base}/${siteId}/domains/custom`,
        { domain },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('configuration du domaine custom'));
  }

  verifyDomainDns(siteId: string, domainId: string): Observable<SiteDomain> {
    return this.http
      .post<SiteDomain>(
        `${this.base}/${siteId}/domains/${domainId}/verify-dns`,
        {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('vérification DNS'));
  }

  deleteDomain(
    siteId: string,
    domainId: string,
  ): Observable<{ ok: boolean; deleted: string; domain?: string }> {
    return this.http
      .delete<{ ok: boolean; deleted: string; domain?: string }>(
        `${this.base}/${siteId}/domains/${domainId}`,
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors('suppression du domaine'));
  }

  setPrimaryDomain(siteId: string, domainId: string): Observable<SiteDomain> {
    return this.http
      .post<SiteDomain>(
        `${this.base}/${siteId}/domains/${domainId}/set-primary`,
        {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('définition du domaine principal'));
  }

  refreshDomainSsl(siteId: string, domainId: string): Observable<SiteDomain> {
    return this.http
      .post<SiteDomain>(
        `${this.base}/${siteId}/domains/${domainId}/refresh-ssl`,
        {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors('rafraîchissement SSL'));
  }

  searchDomains(
    siteId: string,
    q: string,
  ): Observable<{ enabled: boolean; results: DomainSearchResult[]; detail?: string }> {
    return this.http
      .get<{ enabled: boolean; results: DomainSearchResult[]; detail?: string }>(
        `${this.base}/${siteId}/domains/search?q=${encodeURIComponent(q)}`,
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors('recherche de domaines'));
  }

  purchaseDomain(
    siteId: string,
    domain: string,
    years = 1,
  ): Observable<SiteDomain & { charged_credits?: number; balance?: number }> {
    return this.http
      .post<SiteDomain & { charged_credits?: number; balance?: number }>(
        `${this.base}/${siteId}/domains/purchase`,
        { domain, years },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("achat du domaine"));
  }

  /** Détail complet du site (settings, domaines, boutiques liées). */
  getSiteDetail(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.base}/${id}`, { headers: this.createAuthHeaders() })
      .pipe(this.withErrors('chargement du site'));
  }

  /** Suggestions d'amélioration priorisées (chips dynamiques du studio). */
  getSuggestions(
    siteId: string,
  ): Observable<{ ok: boolean; count: number; suggestions: SiteSuggestion[] }> {
    return this.http
      .get<{ ok: boolean; count: number; suggestions: SiteSuggestion[] }>(
        `${this.base}/${siteId}/ai/suggestions`,
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors('chargement des suggestions'));
  }

  /** Téléverse une image de référence et renvoie son URL publique. */
  uploadReferenceImage(file: File): Observable<{ url: string; filename: string }> {
    const form = new FormData();
    form.append('files', file, file.name);
    return this.http
      .post<{ files: Array<{ url: string; filename: string }>; errors: any[] }>(
        `${this.cfg.apiUrl}/files/upload`,
        form,
        { headers: this.createAuthHeaders() },
      )
      .pipe(
        map((res) => {
          const f = res?.files?.[0];
          if (!f?.url) throw new Error('upload sans URL');
          return { url: f.url, filename: f.filename };
        }),
        this.withErrors("téléversement de l'image"),
      );
  }

  // ── AI Threads & Messages ────────────────────────────────────────────
  listThreads(siteId: string): Observable<SiteAIThread[]> {
    return this.http
      .get<SiteAIThread[]>(`${this.base}/${siteId}/ai/threads/`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des conversations'));
  }

  createThread(siteId: string, title?: string): Observable<SiteAIThread> {
    return this.http
      .post<SiteAIThread>(
        `${this.base}/${siteId}/ai/threads/`,
        title ? { title } : {},
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("création d'une conversation"));
  }

  listMessages(siteId: string, threadId: string): Observable<SiteAIMessage[]> {
    return this.http
      .get<SiteAIMessage[]>(
        `${this.base}/${siteId}/ai/threads/${threadId}/messages`,
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors('chargement des messages'));
  }

  /**
   * Annule le run d'arrière-plan en cours pour ce thread (bouton « Stop »).
   * Le run agent est détaché de la requête SSE : fermer le stream côté client
   * ne l'arrête pas — il faut cet appel explicite.
   */
  cancelRun(siteId: string, threadId: string): Observable<{ cancelled: boolean }> {
    return this.http
      .post<{ cancelled: boolean }>(
        `${this.base}/${siteId}/ai/threads/${threadId}/run/cancel`,
        {},
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors("arrêt de la génération"));
  }

  /**
   * Soumet un message utilisateur et reçoit le message persisté + la réponse
   * assistant (stub Phase 0). En Phase 2, sera remplacé par un EventSource SSE.
   */
  submitAIMessage(
    siteId: string,
    threadId: string,
    content: string,
  ): Observable<{ user: SiteAIMessage; assistant: SiteAIMessage }> {
    return this.http
      .post<{ user: SiteAIMessage; assistant: SiteAIMessage }>(
        `${this.base}/${siteId}/ai/threads/${threadId}/messages`,
        { content },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("envoi du message"));
  }

  escalate(siteId: string, threadId: string, reason: string): Observable<any> {
    return this.http
      .post(
        `${this.base}/${siteId}/ai/threads/${threadId}/escalate`,
        { reason },
        { headers: this.jsonHeaders() },
      )
      .pipe(this.withErrors("escalade vers un agent humain"));
  }

  getTools(siteId: string): Observable<SiteAITool[]> {
    return this.http
      .get<SiteAITool[]>(`${this.base}/${siteId}/ai/tools`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors("chargement de la toolbox"));
  }

  /** Balance crédits + tiers de modèles du studio (chip UI + gating premium). */
  getStudioCredits(siteId: string): Observable<StudioCredits> {
    return this.http
      .get<StudioCredits>(`${this.base}/${siteId}/ai/credits`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des crédits IA'));
  }

  /**
   * Streame la réponse AI via SSE. Émet des events successifs au
   * callback `onEvent` :
   *   - `thinking`         : { thread_id, user_msg_id }
   *   - `delta`            : { chunk } — texte token-par-token
   *   - `tool`             : { name, ok, result?, error?, summary? }
   *   - `preview_refresh`  : { site_id } — déclenche le hot-reload iframe
   *   - `done`             : { user, assistant } — messages persistés
   *   - `error`            : { error }
   *
   * Pourquoi `fetch` + ReadableStream plutôt que `EventSource` natif :
   * `EventSource` ne supporte ni POST ni headers Authorization. On parse
   * donc le format SSE à la main (events séparés par `\n\n`).
   *
   * Retourne `{ close }` pour annuler le stream (utile dans ngOnDestroy
   * ou si l'utilisateur quitte la page).
   */
  streamAIMessage(
    siteId: string,
    threadId: string,
    content: string,
    onEvent: (type: string, data: any) => void,
    modelTier: string = 'standard',
    reconnect: boolean = false,
    mode: string = 'agent',
  ): { close: () => void } {
    const url = `${this.base}/${siteId}/ai/threads/${threadId}/messages/stream`;
    const controller = new AbortController();
    const token = this.cookies.get('access_token');

    // Watchdog IDLE (pas un timeout absolu). Un tour agentique Opus 4.8 (lectures/
    // écritures MCP, navigateur, gros builds e-commerce) peut durer plusieurs
    // minutes. Un timeout fixe (ancien 200s) tronquait ces builds → fausse bulle
    // « Envoi impossible » alors que le backend renvoie 200 et continue.
    // On ne coupe donc QUE si AUCUNE donnée n'arrive pendant IDLE_LIMIT_MS — or
    // sse-starlette émet un ping keep-alive toutes les ~15s même pendant un
    // appel LLM long (tâche ping indépendante du générateur). Tant que ces
    // pings/deltas/agent_step arrivent, le stream est vivant et on n'abandonne pas.
    const IDLE_LIMIT_MS = 75_000;
    let lastActivity = Date.now();
    let closed = false;
    const watchdog = setInterval(() => {
      if (closed) return;
      if (Date.now() - lastActivity > IDLE_LIMIT_MS) {
        closed = true;
        clearInterval(watchdog);
        try { controller.abort(); } catch {}
        onEvent('error', {
          error: 'Le serveur ne répond plus (aucune donnée depuis 75 s).',
          code: 'idle_timeout',
        });
      }
    }, 5_000);

    void (async () => {
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify({ content, model_tier: modelTier, reconnect, mode }),
          signal: controller.signal,
        });
        lastActivity = Date.now();
        if (!resp.ok || !resp.body) {
          // 401/403 = session expirée (JWT) → l'UI doit proposer la reconnexion
          // plutôt qu'un trompeur « vérifiez votre connexion ».
          const unauth = resp.status === 401 || resp.status === 403;
          onEvent('error', {
            error: unauth ? 'Session expirée — reconnectez-vous.' : `HTTP ${resp.status}`,
            code: unauth ? 'unauthorized' : `http_${resp.status}`,
            status: resp.status,
          });
          return;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Boucle de lecture jusqu'à fermeture du stream serveur.
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          // Toute donnée reçue (event SSE OU ping keep-alive) = stream vivant.
          lastActivity = Date.now();
          // Normalise CRLF→LF : sse-starlette sépare ses frames par `\r\n\r\n`,
          // or on découpe sur `\n\n`. Sans normalisation, `indexOf('\n\n')` ne
          // matche jamais → aucun event parsé (même pas `done`) → spinner
          // infini. On normalise tout le buffer (robuste si un `\r\n` est coupé
          // entre deux chunks).
          buffer = (buffer + decoder.decode(value, { stream: true })).replace(
            /\r\n/g,
            '\n',
          );
          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            let eventType = 'message';
            let dataLine = '';
            for (const line of raw.split('\n')) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                // Concatène les `data:` multiples (cas SSE multi-ligne).
                dataLine += line.slice(5).trim();
              }
            }
            if (dataLine) {
              try {
                onEvent(eventType, JSON.parse(dataLine));
              } catch {
                onEvent(eventType, dataLine);
              }
            }
          }
        }
      } catch (e: any) {
        // Une annulation volontaire (AbortError) ne doit pas émettre d'error.
        if (e?.name === 'AbortError') return;
        onEvent('error', { error: String(e?.message || e), code: 'network' });
      } finally {
        closed = true;
        clearInterval(watchdog);
      }
    })();

    return { close: () => { closed = true; clearInterval(watchdog); controller.abort(); } };
  }

  // ── AI Provider settings (admin only) ─────────────────────────────
  getAIProviderSettings(): Observable<any> {
    return this.http
      .get(`${this.base}/ai/provider`, { headers: this.createAuthHeaders() })
      .pipe(this.withErrors('chargement des paramètres provider AI'));
  }

  listAIProviderModels(): Observable<{
    providers: Array<{
      id: string;
      label: string;
      models: Array<{ id: string; label: string; tier: string }>;
    }>;
  }> {
    return this.http
      .get<any>(`${this.base}/ai/provider/models`, { headers: this.createAuthHeaders() })
      .pipe(this.withErrors('chargement du catalogue de modèles'));
  }

  updateAIProviderSettings(patch: any): Observable<any> {
    return this.http
      .patch(`${this.base}/ai/provider`, patch, { headers: this.jsonHeaders() })
      .pipe(this.withErrors('mise à jour du provider AI'));
  }

  // ── Site revisions (versioning DB-based) ──────────────────────────
  listRevisions(siteId: string, limit = 30): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.base}/${siteId}/revisions/?limit=${limit}`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors("chargement de l'historique"));
  }

  rollbackToRevision(siteId: string, revisionId: string): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/revisions/${revisionId}/rollback`, {}, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors('restauration de la révision'));
  }

  undoLastChange(siteId: string): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/revisions/undo`, {}, { headers: this.jsonHeaders() })
      .pipe(this.withErrors("annulation de la dernière modification"));
  }

  // ── Thread management (unescalate) ────────────────────────────────
  unescalateThread(siteId: string, threadId: string): Observable<any> {
    return this.http
      .post(`${this.base}/${siteId}/ai/threads/${threadId}/unescalate`, {}, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors("annulation de l'escalade"));
  }

  // ── AI Secrets (secrets vendeur injectés dans le system prompt) ────
  listSecrets(siteId: string): Observable<VendorAISecret[]> {
    return this.http
      .get<VendorAISecret[]>(`${this.base}/${siteId}/ai/secrets`, {
        headers: this.createAuthHeaders(),
      })
      .pipe(this.withErrors('chargement des secrets IA'));
  }

  upsertSecret(
    siteId: string,
    data: { key: string; label: string; value: string; description?: string },
  ): Observable<VendorAISecret> {
    return this.http
      .post<VendorAISecret>(`${this.base}/${siteId}/ai/secrets`, data, {
        headers: this.jsonHeaders(),
      })
      .pipe(this.withErrors("enregistrement du secret IA"));
  }

  deleteSecret(siteId: string, key: string): Observable<{ deleted: boolean }> {
    return this.http
      .delete<{ deleted: boolean }>(
        `${this.base}/${siteId}/ai/secrets/${encodeURIComponent(key)}`,
        { headers: this.createAuthHeaders() },
      )
      .pipe(this.withErrors('suppression du secret IA'));
  }
}
